import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const escalationErrorTypeValues = [
  "ESCALATION_NOT_FOUND",
  "ESCALATION_ALREADY_ACTIVE",
  "ESCALATION_INVALID_TRANSITION",
  "ESCALATION_INVALID_SUB_STATUS",
  "ESCALATION_CONCURRENT_MODIFICATION",
  "ESCALATION_INVALID_CONTRACTOR",
  "ESCALATION_INVALID_RESPONSIBLE_PARTY",
  "ESCALATION_CYCLE_ALREADY_CLOSED",
  "ESCALATION_CYCLE_NOT_FOUND",
  "ESCALATION_REOPEN_FAILED",
  "WORKSITE_NOT_ACCESSIBLE",
  "ESCALATION_WORKSITE_UNSCORED",
] as const;
const escalationErrorTypeSchema = z.enum(escalationErrorTypeValues);
const EscalationErrorType = escalationErrorTypeSchema.enum;
type EscalationErrorType = z.infer<typeof escalationErrorTypeSchema>;

export type EscalationErrors =
  | { errorType: typeof EscalationErrorType.ESCALATION_NOT_FOUND; data: { escalationId: string } }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_ALREADY_ACTIVE;
      data: { workSiteId: number; existingEscalationId: string };
    }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_INVALID_TRANSITION;
      data: { escalationId: string; currentStage: string; targetStage: string; reason: string };
    }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_INVALID_SUB_STATUS;
      data: { subStatus: string; stage: string };
    }
  | { errorType: typeof EscalationErrorType.ESCALATION_CONCURRENT_MODIFICATION; data: { escalationId: string } }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_INVALID_CONTRACTOR;
      data: { escalationId: string; organizationId: string };
    }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_INVALID_RESPONSIBLE_PARTY;
      data: { escalationId: string; organizationIds: string[] };
    }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_CYCLE_ALREADY_CLOSED;
      data: { cycleId: string; escalationId: string };
    }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_CYCLE_NOT_FOUND;
      data: { cycleId: string; escalationId: string };
    }
  | { errorType: typeof EscalationErrorType.ESCALATION_REOPEN_FAILED; data: { escalationId: string; reason: string } }
  | { errorType: typeof EscalationErrorType.WORKSITE_NOT_ACCESSIBLE; data: { workSiteId: number } }
  | {
      errorType: typeof EscalationErrorType.ESCALATION_WORKSITE_UNSCORED;
      data: { workSiteId: number; escalationId: string };
    };

export const escalationErrorDetails: Record<EscalationErrorType, ErrorDetails> = {
  ESCALATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Escalation not found",
  },
  ESCALATION_ALREADY_ACTIVE: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Worksite already has an active escalation",
  },
  ESCALATION_INVALID_TRANSITION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid escalation stage transition",
  },
  ESCALATION_INVALID_SUB_STATUS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Sub-status not valid for escalation stage",
  },
  ESCALATION_CONCURRENT_MODIFICATION: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Escalation was modified by another user",
  },
  ESCALATION_INVALID_CONTRACTOR: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Organization is not a valid contractor for this escalation's worksite",
  },
  ESCALATION_INVALID_RESPONSIBLE_PARTY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Organization is not a valid responsible-party candidate for this escalation's worksite",
  },
  ESCALATION_CYCLE_ALREADY_CLOSED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Escalation cycle is already closed",
  },
  ESCALATION_CYCLE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Escalation cycle not found",
  },
  ESCALATION_REOPEN_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Escalation reopen operation failed",
  },
  WORKSITE_NOT_ACCESSIBLE: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Worksite not accessible to caller",
  },
  ESCALATION_WORKSITE_UNSCORED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Active escalation points to an unscored worksite",
  },
};
